import type { ContentBundle } from "../api/types";

interface Props {
  bundle: ContentBundle;
  /** Visitor's requested locale (may differ from any items' fallback). */
  requestedLocale: string;
  /** Display name for the requested locale, if known. */
  requestedLabel?: string;
}

/** Returns the fallback locale used by the bundle, or null if no fallback. */
function detectFallback(bundle: ContentBundle): string | null {
  if (bundle.letter_fallback_locale) return bundle.letter_fallback_locale;
  for (const args of Object.values(bundle.arguments_by_category)) {
    for (const a of args) if (a.used_fallback_locale) return a.used_fallback_locale;
  }
  for (const f of bundle.faqs) if (f.used_fallback_locale) return f.used_fallback_locale;
  for (const s of bundle.steps) if (s.used_fallback_locale) return s.used_fallback_locale;
  if (bundle.story?.used_fallback_locale) return bundle.story.used_fallback_locale;
  return null;
}

export function FallbackBanner({ bundle, requestedLocale, requestedLabel }: Props) {
  const fallbackLocale = detectFallback(bundle);
  if (!fallbackLocale) return null;

  const messages: Record<string, { de: string; en: string }> = {
    body: {
      de: `Wir haben noch keine vollständige Übersetzung in „${
        requestedLabel ?? requestedLocale.toUpperCase()
      }". Bis dahin zeigen wir den englischen Originaltext, markiert mit dem Sprachkürzel.`,
      en: `We don't have a full ${
        requestedLabel ?? requestedLocale.toUpperCase()
      } translation yet. Showing the English original below — tagged with the language code.`,
    },
    hint: {
      de: "Übersetzung folgt",
      en: "Translation pending",
    },
  };
  const m = (key: keyof typeof messages) =>
    messages[key][requestedLocale as "de" | "en"] ?? messages[key].en;

  return (
    <div className="fallback-banner" role="status">
      <span className="fallback-banner__chip mono">
        {requestedLocale.toUpperCase()} → {fallbackLocale.toUpperCase()}
      </span>
      <span className="fallback-banner__msg">{m("body")}</span>
      <span className="fallback-banner__hint mono">{m("hint")}</span>
    </div>
  );
}
